import {ApolloClient} from "@apollo/client"
import {removeTypenameFromVariables} from "@apollo/client/link/remove-typename"
import {createUploadLink} from "apollo-upload-client"
import {InMemoryCache} from "@apollo/client/cache"
import {onError} from "@apollo/client/link/error"
import buildAxiosFetch from "../../axios-fetch"
import graphFetch from "../graph/fetch"
import {getUrl} from "../graph"
import channel from "../../channel"
import type {IntlCtx} from "../../lang/types"
import typeDefs from "./schema"
import {apolloLogger} from "./middleware/apolloLogger"
import {apolloErrorHandlerLink} from "./middleware/errorHandler"
import {paginateOffset} from "./helpers/paginateType"

export const cache = new InMemoryCache({
  // By default apollo looks for `id` and `_id` but we use `uuid` a lot
  dataIdFromObject: (obj) =>
    !obj.uuid && !obj.id
      ? false // missing cacheable keys so store in parent
      : obj.__typename + ":" + (obj.uuid || obj.id),

  /*
   * Control how types are stored in cache
   *
   * This is only needed if the type returned lacks a `uuid` or `id` field,
   * which is extracted via the `dataIdFromObject` function above
   *
   * @see https://www.apollographql.com/docs/react/caching/cache-configuration#customizing-cache-ids
   */
  typePolicies: {
    // Deals can be returned as perks or rewards for the same uuid with different pricing.
    // Compose the key from uuid + the first product's sale currency so both variants coexist.
    Deal: {
      keyFields(deal) {
        const currency = (deal as any).productConfig?.products?.[0]?.salePrice?.currency ?? ""
        return `Deal:${deal.uuid}:${currency}`
      }
    },

    // don't cache products for reasons TODO: what are reasons?
    Product: {keyFields: false},

    // don't cache snapshots for reasons TODO: what are reasons?
    DealProductSnapshot: {keyFields: false},

    // don't recall PayrollOrder as when view is called it gets all the fields, but list only returns a compacted version which is overriding the view request
    PayrollOrder: {keyFields: false},

    // budget uuids are per budget, but they are specific to each wallet
    Budget: {keyFields: ["walletId", "uuid"]},
    BudgetUnassigned: {keyFields: ["walletId", "uuid"]},

    Query: {
      /* @see https://www.apollographql.com/docs/react/caching/advanced-topics#cache-redirects */
      fields: {
        poll: {read: (_, {args, toReference}) => toReference({uuid: args?.id, __typename: "Poll"})},
        recognition: {read: (_, {args, toReference}) => toReference({uuid: args?.id, __typename: "Recognition"})},
        wellnessSearch: paginateOffset(),
        advancedBudgets: {
          merge(existing, incoming) {
            return {...(existing || {}), ...incoming}
          }
        },
        // ListSurveys is paginated via an opaque ES cursor (searchAfter), not offset — keep a single
        // cache entry and append pages, rather than one entry per searchAfter as the default keyArgs would.
        surveys: {
          keyArgs: false,
          merge(existing: any, incoming: any, {args}) {
            if (!args?.searchAfter || !existing) return incoming
            return {...incoming, surveys: [...existing.surveys, ...incoming.surveys]}
          }
        }
      }
    },
    AdvancedBudgetsQueries: {
      fields: {
        listBudgets: paginateOffset({keyArgsOmit: ["offset", "limit"]}),
        listWallets: paginateOffset({keyArgsOmit: ["offset", "limit"]}),
        rewardWalletWithBudgets: paginateOffset({keyArgsOmit: ["offset", "limit"]}),
        getBudgetManagers: paginateOffset({keyArgsOmit: ["offset", "limit"]})
      }
    },
    BudgetDetails: {
      keyFields: ["budget", ["uuid"]]
    },
    // @see https://www.apollographql.com/docs/react/caching/cache-field-behavior#merging-arrays
    Payment: {
      fields: {
        details: {
          merge(existing: any = {}, incoming: any) {
            return {...existing, ...incoming}
          }
        }
      }
    },
    CheckoutRedemption: {
      fields: {
        order: {
          // @see https://www.apollographql.com/docs/react/caching/cache-field-behavior#merging-non-normalized-objects
          merge(existing = {}, incoming, {mergeObjects}) {
            return mergeObjects(existing, incoming)
          }
        }
      }
    }
  }
})

/**
 * The wrapper Error that will be returned from any Apollo component
 * or hook
 */
export interface PBClientApolloError extends Error {
  graphQLErrors: []
  networkError: GraphError
}

/** local variable binding to give access to intl formatters */
const intlStore: {intl: IntlCtx | null} = {intl: null}

/**
 * This is a bit of a hacky workaround to add intl.formatMessage to apollo.client to format errors
 * ideally the client would be initialised in the provider, but sadly it's a singleton that is imported elsewhere and needs refactoring
 */
export function setIntl(i: IntlCtx) {
  intlStore.intl = i
}

const client = new ApolloClient({
  defaultContext: {
    intl: intlStore
  },
  // an apollo 'link' is like middleware, you build up a link of
  // functions and the requests flow through and then return back
  // along the link, so order is important!
  link:
    // NOTE: inlining because it's important
    // this must come first to capture any errors that occured and
    // feed them down into components or hooks
    // this will return any errors in a PBClientApolloError
    //
    // See the apolloErrorHandler for how we throw an errors the occurred
    // further up the link
    onError(({graphQLErrors, networkError, operation}) => {
      if (graphQLErrors) {
        // empty for now
      }

      // everything is treated as a network error because of how we handle things
      // in the link and graphFetch... but the apolloErrorHandlerLink isn't
      // always hit! So this final link will always receive a network error which
      // is our marshalled error type. It stores the message and errorData which
      // can then be passed on to things like the Error component.
      // In practice people struggle with this concept, and try to handle the
      // error themselves, so instead what we can do is try to format the message
      // here and return it. The `createUseQuery` et all hooks will return this
      // under the `errorMsg` value for easy use in app code
      if (networkError) {
        const err = networkError as GraphError
        console.log("[Network error]:", err)
        const intl = operation.getContext()?.intl?.intl
        if (intl?.formatMessage) {
          if (err?.message && err?.errorData) {
            err.formattedMessage = intl.formatMessage({id: err.message}, err.errorData)
          }
        }
      }
    })
      .concat(apolloErrorHandlerLink)
      .concat(apolloLogger())
      .concat(removeTypenameFromVariables())
      .concat(
        createUploadLink({
          uri: getUrl,
          fetch: (input, init = {}) => {
            // Only use native fetch for FormData uploads on Android
            const isFormDataUpload = init?.body && init.body instanceof FormData
            const isAndroid = channel === "android" // Platform-specific via build system
            if (isFormDataUpload && isAndroid) {
              // For Android FormData uploads, use native fetch
              const graphFetchInstance = graphFetch({
                isAndroidFileUpload: true,
                isFileUpload: true
              })
              return graphFetchInstance
                .request({
                  url: input,
                  method: init.method || "GET",
                  data: init.body,
                  headers: init.headers || {}
                })
                .then((apiResponse) => {
                  // Convert API response back to fetch Response format for Apollo
                  const responseBody = typeof apiResponse.data === "object" ? JSON.stringify(apiResponse.data) : apiResponse.data

                  const headers = new Headers()
                  Object.entries(apiResponse.headers).forEach(([key, value]) => {
                    headers.append(key, String(value))
                  })

                  return new Response(responseBody, {
                    status: apiResponse.status,
                    statusText: apiResponse.statusText,
                    headers
                  })
                })
            }

            // For regular requests, use the existing buildAxiosFetch flow
            return buildAxiosFetch(graphFetch({isFileUpload: isFormDataUpload}))(input, init)
          }
        })
      ),
  cache,
  typeDefs
})

export default client
